// api2.js
//
// The one request helper for api2.govglance.org: parties, elections,
// spending, companies, disclosures and members' records all go through it.
//
// Requests are cached by URL for ten minutes, as promises, so panels that ask
// the same question share one request. api2's own data moves daily at the
// fastest (spending is cached upstream for six hours to a day), so a session
// never needs a fresher copy than that. A failed request is dropped from the
// cache so the next mount retries instead of replaying the failure.
//
// No AbortSignal is passed to fetch: a cached promise is shared, and aborting
// it on one component's unmount would reject it for every other consumer.
// Components ignore late answers instead (see useApi2Resource).
//
// An error carries the API's own `detail` where it gives one. api2 explains
// its 4xx answers in plain words -- which filter was bad, that a dataset is
// not loaded on this host yet -- and that sentence is more use to a reader
// than a status code.

export const API2_BASE = process.env.REACT_APP_API2_URL || 'https://api2.govglance.org';

const CACHE_TTL_MS = 10 * 60 * 1000;
const cache = new Map();

export class Api2Error extends Error {
  constructor(message, status, detail = null) {
    super(message);
    this.name = 'Api2Error';
    this.status = status;
    this.detail = detail;
    this.notFound = status === 404;
    // 502: USAspending did not answer. 503: the dataset is not loaded yet.
    // Both are worth a "try again later" rather than a "nothing here".
    this.upstream = status === 502 || status === 503;
  }
}

const buildUrl = (path, params = {}) => {
  const url = new URL(path, API2_BASE);
  Object.entries(params).forEach(([key, value]) => {
    if (value === undefined || value === null || value === '') return;
    url.searchParams.set(key, String(value));
  });
  return url.toString();
};

export const fetchApi2 = (path, params) => {
  const url = buildUrl(path, params);
  const hit = cache.get(url);
  if (hit && Date.now() - hit.at < CACHE_TTL_MS) return hit.promise;

  const promise = fetch(url, { headers: { Accept: 'application/json' } })
    .then(async (response) => {
      if (!response.ok) {
        const body = await response.json().catch(() => null);
        const detail = body && typeof body.detail === 'string' ? body.detail : null;
        throw new Api2Error(detail || `api2 ${response.status} for ${path}`, response.status, detail);
      }
      return response.json();
    })
    .catch((error) => {
      cache.delete(url);
      throw error;
    });

  cache.set(url, { promise, at: Date.now() });
  return promise;
};